import React, { useState, useMemo } from 'react';
import { useT } from '../lib/i18n';
import { Search, Link2, X, Sparkles, Boxes, Tags } from 'lucide-react';
import PageShell from '../components/PageShell';
import { SectionCard, KpiTile, PillToggle, PageHeader, INPUT, Loader, ErrorBox } from '../components/charts';
import { useApi } from '../hooks/useApi';
import api from '../lib/api';
import { formatNumber } from '../lib/format';

// backend source ids → what the owners call them
const SOURCE_LABEL = { lightspeed: 'LightSpeed', odoo: 'Odoo' };

/** Inline "save failed" note for the admin actions (mapping / category remap). */
const SaveError = ({ message, onClose }) => {
  const t = useT();
  if (!message) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700 mb-3">
      <span>{t('Opslaan mislukt.')} {message}</span>
      <button onClick={onClose} className="text-red-400 hover:text-red-700" aria-label={t('Sluiten')}><X className="h-4 w-4" /></button>
    </div>
  );
};
const errMessage = (e) => e?.response?.data?.detail || e?.message || '';

export default function ProductManagement() {
  const t = useT();
  const [tab, setTab] = useState('products');
  return (
    <PageShell page="Productkoppeling" accent="green">
      <PageHeader title={t("Productkoppeling")}
        subtitle={<><span className="text-slate-400">{t('(beheerder)')}</span> {t("Koppel producten over de systemen heen en beheer de categorieën.")}</>}>
        <PillToggle value={tab} onChange={setTab}
          options={[{ value: 'products', label: t('Producten') }, { value: 'categories', label: t('Categorieën') }]} />
      </PageHeader>
      {tab === 'products' ? <ProductsTab /> : <CategoriesTab />}
    </PageShell>
  );
}

/* ----------------------------------------------------------- Products tab */
function ProductsTab() {
  const t = useT();
  const { data, loading, error, reload } = useApi(() => api.mapping(), []);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('lightspeed');
  const [savingId, setSavingId] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const odooById = useMemo(() => {
    const m = {};
    (data?.odoo_products || []).forEach((o) => (m[o.product_id] = o));
    return m;
  }, [data]);

  const save = async (body) => {
    setSavingId(body.product_id);
    setSaveError(null);
    try { await api.setMapping(body); await reload(); }
    catch (e) { setSaveError(errMessage(e)); }
    finally { setSavingId(null); }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const ql = q.trim().toLowerCase();
    return data.products.filter((p) => {
      if (filter === 'lightspeed' && p.source !== 'lightspeed') return false;
      if (filter === 'unmapped' && (p.mapped_to || p.override_l1)) return false;
      if (ql && !p.name.toLowerCase().includes(ql)) return false;
      return true;
    });
  }, [data, q, filter]);

  const categories = data?.categories || {};
  const l1Options = Object.keys(categories);

  if (loading) return <Loader />;
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return null;

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiTile label={t("Producten totaal")} value={formatNumber(data.counts.total)} accent="gold" icon={Boxes} />
        <KpiTile label={t("Uit de kassa")} sub="LightSpeed" value={formatNumber(data.counts.lightspeed)} accent="mint" />
        <KpiTile label={t("Gekoppeld")} value={formatNumber(data.counts.mapped)} accent="green" />
        <KpiTile label={t("Categorie overschreven")} value={formatNumber(data.counts.overridden)} accent="gold" />
      </div>

      {/* browser autocomplete for the link input (suggestions only; matching happens on "Koppel") */}
      <datalist id="odoo-products">
        {data.odoo_products.map((o) => (
          <option key={o.product_id} value={`${o.name} · ${o.category_l1}${o.category_l2 ? '/' + o.category_l2 : ''}`} />
        ))}
      </datalist>

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Zoek product…")}
            className={`${INPUT} w-full pl-9`} />
        </div>
        <PillToggle value={filter} onChange={setFilter}
          options={[{ value: 'lightspeed', label: t('Uit de kassa') }, { value: 'unmapped', label: t('Nog te doen') }, { value: 'all', label: t('Alle') }]} />
      </div>

      <SaveError message={saveError} onClose={() => setSaveError(null)} />

      <SectionCard>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left border-b border-slate-100">
                <th className="py-2 font-medium">{t("Product")}</th>
                <th className="font-medium text-right">{t("Stuks")}</th>
                <th className="font-medium">{t("Gebruikte categorie")}</th>
                <th className="font-medium" title="Odoo">{t("Zelfde product in het oude systeem")}</th>
                <th className="font-medium">{t("Of: stel categorie in")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 400).map((p) => (
                <Row key={p.product_id} p={p} odooById={odooById} l1Options={l1Options}
                  categories={categories} saving={savingId === p.product_id} onSave={save} />
              ))}
            </tbody>
          </table>
          {rows.length > 400 && <p className="text-xs text-slate-400 mt-2">{t("Eerste 400 van")} {rows.length} — {t("verfijn met zoeken.")}</p>}
          {!rows.length && <p className="text-sm text-slate-400 py-3">{t("Niets gevonden.")}</p>}
        </div>
      </SectionCard>
    </>
  );
}

function Row({ p, odooById, l1Options, categories, saving, onSave }) {
  const t = useT();
  const mappedName = p.mapped_to && odooById[p.mapped_to]?.name;
  const [link, setLink] = useState('');
  const [miss, setMiss] = useState(false);

  // exact (case-insensitive) name match on the typed text; datalist entries carry " · category" after the name
  const resolveLink = () => {
    const name = link.split(' · ')[0].trim().toLowerCase();
    if (!name) return;
    const hit = Object.values(odooById).find((o) => o.name.toLowerCase() === name);
    if (hit) { setMiss(false); onSave({ product_id: p.product_id, mapped_to: hit.product_id }); }
    else setMiss(true);
  };
  const sourceTitle = p.source === 'lightspeed' ? t('Product uit de LightSpeed-kassa') : t('Product uit het oude systeem (Odoo)');

  return (
    <tr className={`border-b border-slate-50 ${saving ? 'opacity-50' : ''}`}>
      <td className="py-2 text-slate-700">
        {p.name}
        <span title={sourceTitle} className={`ml-1.5 text-[9px] px-1 py-0.5 rounded cursor-help ${p.source === 'lightspeed' ? 'bg-[#8bd4be]/30 text-[#114817]' : 'bg-slate-100 text-slate-500'}`}>{p.source === 'lightspeed' ? 'LS' : 'Odoo'}</span>
        <div className="text-[10px] text-slate-400">{t("eigen")}: {p.own_l1}{p.own_l2 ? '/' + p.own_l2 : ''}</div>
      </td>
      <td className="text-right tabular-nums text-slate-500">{formatNumber(p.units)}</td>
      <td><span className="inline-block px-2 py-0.5 rounded-md bg-[#cf9a40]/15 text-[#7a5a1e] text-xs">{p.eff_l1}{p.eff_l2 ? ' / ' + p.eff_l2 : ''}</span></td>
      <td>
        {mappedName ? (
          <span className="inline-flex items-center gap-1 text-xs text-[#114817]"><Link2 className="h-3 w-3" /> {mappedName}</span>
        ) : (
          <div>
            <div className="flex items-center gap-1">
              <input list="odoo-products" value={link} placeholder={t("naam in het oude systeem…")}
                onChange={(e) => { setLink(e.target.value); setMiss(false); }}
                onKeyDown={(e) => { if (e.key === 'Enter') resolveLink(); }}
                className={`${INPUT} w-44 ${miss ? 'border-red-300' : ''}`} />
              <button onClick={resolveLink} disabled={!link.trim() || saving}
                className="rounded-md bg-[#114817] text-white text-xs font-medium px-2.5 py-2 sm:py-1.5 hover:bg-[#186020] disabled:opacity-40">
                {t('Koppel')}
              </button>
              {p.suggested_odoo_id && odooById[p.suggested_odoo_id] && (
                <button title={`${t("Voorstel")}: ${odooById[p.suggested_odoo_id].name}`}
                  onClick={() => onSave({ product_id: p.product_id, mapped_to: p.suggested_odoo_id })}
                  className="inline-flex items-center gap-0.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-0.5 hover:bg-amber-100">
                  <Sparkles className="h-3 w-3" />{odooById[p.suggested_odoo_id].name.slice(0, 14)}
                </button>
              )}
            </div>
            {miss && <div className="text-[10px] text-red-500 mt-0.5">{t('Geen overeenkomst gevonden')}</div>}
          </div>
        )}
      </td>
      <td>
        <div className="flex items-center gap-1">
          <select value={p.override_l1 || ''} onChange={(e) => onSave({ product_id: p.product_id, override_l1: e.target.value, override_l2: '' })}
            className="rounded-md border border-slate-200 px-1 py-1 text-xs outline-none focus:border-[#cf9a40] max-w-[110px]">
            <option value="">—</option>
            {l1Options.map((l1) => <option key={l1} value={l1}>{l1}</option>)}
          </select>
          {p.override_l1 && (categories[p.override_l1] || []).filter(Boolean).length > 0 && (
            <select value={p.override_l2 || ''} onChange={(e) => onSave({ product_id: p.product_id, override_l1: p.override_l1, override_l2: e.target.value })}
              className="rounded-md border border-slate-200 px-1 py-1 text-xs outline-none focus:border-[#cf9a40] max-w-[110px]">
              <option value="">{t("(geen sub)")}</option>
              {(categories[p.override_l1] || []).filter(Boolean).map((l2) => <option key={l2} value={l2}>{l2}</option>)}
            </select>
          )}
        </div>
      </td>
      <td className="text-right">
        {(p.mapped_to || p.override_l1) && (
          <button title={t("Wissen")} onClick={() => onSave({ product_id: p.product_id, clear: true })} className="text-slate-300 hover:text-red-500"><X className="h-4 w-4" /></button>
        )}
      </td>
    </tr>
  );
}

/* --------------------------------------------------------- Categories tab */
function CategoriesTab() {
  const t = useT();
  const { data, loading, error, reload } = useApi(() => api.categories(), []);
  const [saving, setSaving] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const save = async (body) => {
    setSaving(body.from_label);
    setSaveError(null);
    try { await api.setCategoryRemap(body); await reload(); }
    catch (e) { setSaveError(errMessage(e)); }
    finally { setSaving(null); }
  };

  if (loading) return <Loader />;
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return null;

  return (
    <SectionCard title={t("Categorieën beheren")}
      subtitle={t("Hernoem of voeg een (bron)categorie samen met een andere. Geldt overal: catalogus, grafieken en rapportgroepen.")}>
      <SaveError message={saveError} onClose={() => setSaveError(null)} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left border-b border-slate-100">
              <th className="py-2 font-medium">{t("Broncategorie")}</th>
              <th className="font-medium text-right">{t("Producten")}</th>
              <th className="font-medium text-right">{t("Stuks")}</th>
              <th className="font-medium">{t("Bron")}</th>
              <th className="font-medium">{t("Toewijzen aan")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.categories.map((c) => (
              <tr key={c.label} className={`border-b border-slate-50 ${saving === c.label ? 'opacity-50' : ''}`}>
                <td className="py-2 text-slate-700 font-medium">
                  {c.label}
                  {c.remap_to_l1 && <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-[#114817]"><Tags className="h-3 w-3" />→ {c.remap_to_l1}{c.remap_to_l2 ? '/' + c.remap_to_l2 : ''}</span>}
                </td>
                <td className="text-right tabular-nums text-slate-500">{formatNumber(c.products)}</td>
                <td className="text-right tabular-nums text-slate-500">{formatNumber(c.units)}</td>
                <td className="text-xs text-slate-400">{c.sources.map((s) => SOURCE_LABEL[s] || s).join(', ')}</td>
                <td>
                  <div className="flex items-center gap-1">
                    <select value={c.remap_to_l1 || ''} onChange={(e) => save({ from_label: c.label, to_l1: e.target.value, to_l2: '' })}
                      className="rounded-md border border-slate-200 px-1 py-1 text-xs outline-none focus:border-[#cf9a40] max-w-[150px]">
                      <option value="">— {t("(laat staan)")}</option>
                      {data.target_l1s.filter((l1) => l1 !== c.label).map((l1) => <option key={l1} value={l1}>{l1}</option>)}
                    </select>
                  </div>
                </td>
                <td className="text-right">
                  {c.remap_to_l1 && (
                    <button title={t("Toewijzing wissen")} onClick={() => save({ from_label: c.label, clear: true })} className="text-slate-300 hover:text-red-500"><X className="h-4 w-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}
